import React, { useEffect, useState } from "react";
import {
  db, functions, ACTIVITY_TYPES, TERMS, calcProvisionalPoints, getCurrentPeriodId,
  getCurrentTermId, getSubmissionTermId, calcTermScores
} from "./firebase";
import {
  collection, addDoc, query, where, orderBy, onSnapshot, serverTimestamp
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

const checkReport = httpsCallable(functions, "checkReport");


export default function StudentView({ user }) {
  const [submissions, setSubmissions] = useState([]);
  const [form, setForm] = useState({ type: "A", date: "", hours: "", minutes: "", description: "" });
  const [provisionalPoints, setProvisionalPoints] = useState(null);
  const [aiFeedback, setAiFeedback] = useState("");
  const [aiApproved, setAiApproved] = useState(null); // null=未チェック, true=OK, false=NG
  const [aiLoading, setAiLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const currentTermId = getCurrentTermId();
  const [viewTermId, setViewTermId] = useState(currentTermId);
  const isCurrentTerm = viewTermId === currentTermId;

  useEffect(() => {
    const q = query(
      collection(db, "submissions"),
      where("uid", "==", user.uid),
      orderBy("submittedAt", "desc")
    );
    return onSnapshot(q, (snap) => {
      setSubmissions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [user.uid]);

  useEffect(() => {
    if (form.type !== "D") {
      const pts = calcProvisionalPoints(form.type, form.hours, form.minutes);
      setProvisionalPoints(pts);
    } else {
      setProvisionalPoints(null);
    }
  }, [form.type, form.hours, form.minutes]);

  const getAiFeedback = async (description) => {
    if (!description.trim()) return;
    setAiLoading(true);
    setAiFeedback("");
    setAiApproved(null);
    try {
      const res = await checkReport({ description });
      const parsed = res.data;
      setAiFeedback(parsed.feedback);
      setAiApproved(parsed.approved);
    } catch (e) {
      setAiFeedback("AIフィードバックの取得に失敗しました。そのまま提出できます。");
      setAiApproved(true); // APIエラー時はブロックしない
    }
    setAiLoading(false);
  };

  const handleDescriptionBlur = () => {
    if (form.description.trim().length > 5) {
      getAiFeedback(form.description);
    }
  };

  // 内容が変わったらAI判定をリセット
  const handleDescriptionChange = (e) => {
    setForm(f => ({ ...f, description: e.target.value }));
    setAiApproved(null);
    setAiFeedback("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.date) { setError("日時を入力してください。"); return; }
    if (!form.description.trim()) { setError("活動内容を入力してください。"); return; }

    // AIチェック未実施の場合は実施してからブロック
    if (aiApproved === null && form.description.trim().length > 5) {
      setError("内容欄からカーソルを外してAIフィードバックを確認してから提出してください。");
      return;
    }

    // AIがNGと判定した場合はブロック
    if (aiApproved === false) {
      setError("活動内容をもう少し詳しく書き直してから提出してください。");
      return;
    }

    setSubmitting(true);
    try {
      const termId = getCurrentTermId();
      const periodId = getCurrentPeriodId(termId);
      await addDoc(collection(db, "submissions"), {
        uid: user.uid,
        email: user.email,
        name: user.displayName,
        type: form.type,
        date: form.date,
        hours: parseInt(form.hours) || 0,
        minutes: parseInt(form.minutes) || 0,
        description: form.description,
        provisionalPoints: provisionalPoints,
        aiFeedback: aiFeedback,
        aiApproved: aiApproved,
        termId,
        periodId,
        status: "pending",
        finalPoints: null,
        teacherNote: "",
        submittedAt: serverTimestamp(),
      });
      setSuccess("提出しました！");
      setForm({ type: "A", date: "", hours: "", minutes: "", description: "" });
      setProvisionalPoints(null);
      setAiFeedback("");
      setAiApproved(null);
      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError("提出に失敗しました。もう一度試してください。");
    }
    setSubmitting(false);
  };

  // 表示中の学期の集計（繰り越しは学期内のみ）
  const { periods: periodWithCarry, total: totalScore, maxTotal } = calcTermScores(submissions, viewTermId);
  const termSubmissions = submissions.filter(s => getSubmissionTermId(s) === viewTermId);

  return (
    <div className="student-view">
      <div className="term-tabs">
        {TERMS.filter(t => t.id <= currentTermId).map(t => (
          <button
            key={t.id}
            type="button"
            className={`term-tab ${viewTermId === t.id ? "active" : ""}`}
            onClick={() => setViewTermId(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <section className="score-summary">
        <h2>📊 成績サマリー</h2>
        {!isCurrentTerm && <p className="term-note">過去の学期の記録です（閲覧のみ）。</p>}
        <div className="period-grid">
          {periodWithCarry.map(p => (
            <div key={p.id} className={`period-card ${p.effective >= p.maxPoints ? "complete" : ""}`}>
              <div className="period-label">{p.label}</div>
              <div className="period-deadline">{p.deadline.toLocaleDateString("ja-JP", { month: "long", day: "numeric" })}締切</div>
              <div className="period-score">{p.effective} / {p.maxPoints}点</div>
              {p.carryover > 0 && <div className="carryover">繰越 +{p.carryover}点</div>}
              {p.excess > 0 && <div className="excess">翌期繰越 +{p.excess}点</div>}
            </div>
          ))}
        </div>
        <div className="total-score">合計：<strong>{totalScore} / {maxTotal}点</strong></div>
      </section>

      {isCurrentTerm && (
      <section className="form-section">
        <h2>📤 新しい活動を報告する</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>１．英語使用の種類</label>
            {Object.entries(ACTIVITY_TYPES).map(([key, val]) => (
              <label key={key} className="radio-label">
                <input
                  type="radio"
                  name="type"
                  value={key}
                  checked={form.type === key}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                />
                <span>
                  <strong>{val.label}</strong>
                  <small>（{val.unit}）{val.example}</small>
                </span>
              </label>
            ))}
          </div>

          <div className="form-group">
            <label>２．日時</label>
            <input
              type="date"
              value={form.date}
              onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              max={new Date().toISOString().split("T")[0]}
            />
          </div>

          <div className="form-group">
            <label>３．およその英語使用時間</label>
            <div className="time-inputs">
              <input
                type="number" min="0" max="24" placeholder="0"
                value={form.hours}
                onChange={e => setForm(f => ({ ...f, hours: e.target.value }))}
              />
              <span>時間</span>
              <input
                type="number" min="0" max="59" placeholder="0"
                value={form.minutes}
                onChange={e => setForm(f => ({ ...f, minutes: e.target.value }))}
              />
              <span>分</span>
            </div>
            {provisionalPoints !== null && (
              <div className="provisional-points">
                暫定点数：<strong>{provisionalPoints}点</strong>
                <small>（教員確認後に確定します）</small>
              </div>
            )}
            {form.type === "D" && (
              <div className="provisional-points">種類Dは教員が個別に点数を判断します。</div>
            )}
          </div>

          <div className="form-group">
            <label>４．具体的な内容</label>
            <textarea
              rows={4}
              placeholder="新たに覚えた英語の表現など、できれば英語について記述してください。"
              value={form.description}
              onChange={handleDescriptionChange}
            />
            <button
              type="button"
              className="btn-check"
              onClick={() => getAiFeedback(form.description)}
              disabled={aiLoading || !form.description.trim()}
            >
              {aiLoading ? "チェック中..." : "✦ 内容をAIチェックする"}
            </button>
            {aiFeedback && !aiLoading && (
              <div className={`ai-feedback ${aiApproved ? "approved" : "rejected"}`}>
                <strong>{aiApproved ? "✅ AIフィードバック：" : "⚠️ AIフィードバック："}</strong>
                <p>{aiFeedback}</p>
              </div>
            )}
          </div>

          {error && <p className="error">{error}</p>}
          {success && <p className="success">{success}</p>}

          <button type="submit" className="btn-submit" disabled={submitting || aiLoading || aiApproved === false}>
            {submitting ? "提出中..." : aiApproved === false ? "内容を修正してください" : "提出する"}
          </button>
        </form>
      </section>
      )}

      <section className="history-section">
        <h2>📋 提出履歴</h2>
        {termSubmissions.length === 0 ? (
          <p className="empty">まだ提出がありません。</p>
        ) : (
          <div className="submission-list">
            {termSubmissions.map(s => (
              <div key={s.id} className={`submission-card status-${s.status}`}>
                <div className="sub-header">
                  <span className="sub-type">種類{s.type}</span>
                  <span className="sub-date">{s.date}</span>
                  <span className="sub-time">{s.hours}時間{s.minutes}分</span>
                  <span className={`sub-status ${s.status}`}>
                    {s.status === "pending" ? "確認中" : s.status === "approved" ? "承認済" : "修正"}
                  </span>
                </div>
                <p className="sub-description">{s.description}</p>
                <div className="sub-points">
                  暫定{s.provisionalPoints ?? "—"}点
                  {s.finalPoints !== null && s.finalPoints !== undefined && (
                    <strong> → 確定{s.finalPoints}点</strong>
                  )}
                </div>
                {s.submittedAt && (
                  <div className="sub-timestamp">
                    🕐 提出日時：{s.submittedAt.toDate().toLocaleString("ja-JP")}
                  </div>
                )}
                {s.teacherNote && (
                  <div className="teacher-note">📝 先生のコメント：{s.teacherNote}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
